/**
 * The look every board of the thirty shares (Marek, 6 Oct 2026: "designs of the games are not
 * united"). A board takes its colours, lines, corners and type from here and from nowhere else:
 * no red, green or amber of its own, no font written out as a string. Its own art (a ship, a
 * flower, a dial) keeps its own colours; the language around it — right, wrong, careful, the
 * accent, the words — is this one.
 */
import { mono, sans } from '../../../ui/atoms.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';

export { mono, sans };

/**
 * The board's own space. Every board draws in a 320 × 320 SVG box and is shown at the phone's
 * width, so every board is the same size on the same phone (about 1:1 on a small Android, a little
 * larger on an iPhone). Positions in a view state are in these units.
 */
export const BOARD = { w: 320, h: 320 } as const;

/** Words and lines, brightest first. */
export const INK = {
  /** What matters most: the status when a round is won, a number to read. */
  hi: '#ffffff',
  /** Ordinary words and lines. */
  text: '#dfe9f2',
  /** Labels, hints, things at rest. */
  dim: '#9db3c2',
  /** The faintest marks: grid lines, empty slots, a track. */
  faint: '#5d6e7b',
  /** Words on a filled button (the accent, OK). */
  onFill: '#05070a',
} as const;

/** Right: a move that counts, a round won, a gauge in the safe zone. */
export const OK = '#6de39a';
/** Wrong: a mistake. The same red as the station's mistake dots and TRY AGAIN. */
export const BAD = '#ff5f86';
/** Careful: nearly, too fast, wait. */
export const WARN = '#ffb347';

/**
 * Six colours for a game that needs things told apart (wires, seeds, pads, goods), the same six in
 * every era. Picked from a set made for colour-blind eyes and lifted to read on the dark boards;
 * where a colour is the whole point, give each thing a shape too.
 */
export const PALETTE = ['#56b4e9', '#e69f00', '#2bc79a', '#f0e442', '#cc79a7', '#4d8dff'] as const;

/** Line widths, in board units. */
export const STROKE = { hair: 1, line: 2, bold: 3 } as const;

/** Corners, in board units (and px for the frame). */
export const RADIUS = { board: 16, button: 10, chip: 999 } as const;

/** Type sizes, px on the page and units on the board (about the same on a phone). */
export const TYPE = {
  /** The status line above the board. */
  status: 15,
  /** Its hint on the right, and small labels. */
  hint: 12,
  /** A word on an in-board button. */
  button: 16,
  /** A label inside the board. */
  label: 13,
  /** A big number or word inside the board. */
  big: 24,
} as const;

/**
 * The accent, light enough to read as text on the dark boards: the last stop's deep green
 * (#5c7d4e) is mixed toward white until it reads; the bright ones are left alone.
 */
export function readable(accent: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(accent);
  if (!m) return accent;
  const n = parseInt(m[1]!, 16);
  const rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const lin = rgb.map((c) => {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
  if (luminance >= 0.3) return accent;
  const mix = Math.min(0.6, (0.3 - luminance) * 2.2);
  return `#${rgb.map((c) => Math.round(c + (255 - c) * mix).toString(16).padStart(2, '0')).join('')}`;
}

/** The era's accent, made readable: what a board gets as `accent`, and what the TV uses. */
export const accentOf = (era: Era): string => readable(ERA_SKIN[era].ink);

/** `#rrggbb` at an opacity, for fills and glows. */
export function alpha(hex: string, a: number): string {
  const v = Math.round(Math.max(0, Math.min(1, a)) * 255);
  return `${hex.slice(0, 7)}${v.toString(16).padStart(2, '0')}`;
}

/** A number kept short for a view state: whole units by default, or `places` decimals. */
export function q(n: number, places = 0): number {
  const k = 10 ** places;
  return Math.round(n * k) / k;
}

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
